import type { AppShellProps, GroupProps, MantineSize } from '@mantine/core';
import { AppShell, Group, rem, ScrollArea } from '@mantine/core';
import { useDidUpdate } from '@mantine/hooks';
import { Outlet, useLocation } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';

import { LoadingOverlay } from '@apple/ui/loading-overlay';
import { AppleLogo } from '@apple/ui/shell/components/AppleLogo';
import { AvatarMenu } from '@apple/ui/shell/components/AvatarMenu';
import { RightSidebarPortalTarget } from '@apple/ui/shell/components/RightSidebarPortalTarget';
import { ToggleColorSchemeButton } from '@apple/ui/shell/components/ToggleColorSchemeButton';
import { ToggleLeftSidebarButton } from '@apple/ui/shell/components/ToggleLeftSidebarButton';
import { initializeHeader } from '@apple/ui/shell/contexts/header';
import {
	initializeLeftSidebar,
	useLeftSidebarCollapsed,
	useLeftSidebarProps,
} from '@apple/ui/shell/contexts/left-sidebar';
import {
	initializeRightSidebarProps,
	useRightSidebarCollapsed,
	useRightSidebarProps,
} from '@apple/ui/shell/contexts/right-sidebar';
import { useColorScheme } from '@apple/ui/shell/hooks/useColorScheme';
import { getEnv } from '@apple/utils/config/env';

import { AnnouncementMenu } from '@apple/features/announcements/components/AnnouncementMenu';
import { useUserActiveAnnouncements } from '@apple/features/announcements/hooks/useUserAnnouncements';
import { useAuthContext } from '@apple/features/auth/hooks/useAuthContext';

import { AppleFooter } from './components/apple-footer';
import type { MenuItemGroup } from './components/menu-item';
import { SidebarMenu } from './components/sidebar-menu';

const env = getEnv();

type Props = PropsWithChildren<{
	menuItems?: MenuItemGroup[];
	loading?: boolean;
	cartMenu?: React.ReactNode;
	logoLabelSize?: MantineSize | (string & {}) | number;
	fixedHeader?: boolean;
	hideHeaderAndSidebar?: boolean;
}>;

export function AppleLayout({
	children,
	loading = false,
	cartMenu,
	menuItems = [],
	logoLabelSize,
	fixedHeader = false,
	hideHeaderAndSidebar = false,
}: Props) {
	const { authenticated } = useAuthContext();
	const { isDark } = useColorScheme();

	useEffect(() => {
		if (fixedHeader) {
			initializeHeader({ isFixed: true });
			initializeLeftSidebar({ width: 260 });
			initializeRightSidebarProps({ width: 280 });
		}
	}, [fixedHeader]);

	const leftSidebar = useLeftSidebarProps();
	const leftSidebarCollapsed = useLeftSidebarCollapsed();
	const rightSidebarProps = useRightSidebarProps();
	const rightSidebarCollapsed = useRightSidebarCollapsed();
	const location = useLocation();
	useDidUpdate(() => {
		if (leftSidebarCollapsed.isSmallerThanBreakpoint && !leftSidebarCollapsed.isCollapsed) {
			leftSidebarCollapsed.close();
		}
	}, [location]);

	const headerVisible =
		!hideHeaderAndSidebar &&
		(!authenticated || fixedHeader || leftSidebarCollapsed.isCollapsed);
	const leftSidebarVisible = !hideHeaderAndSidebar && menuItems.length > 0;

	const appshellProps = useMemo<AppShellProps>(
		() => ({
			header: {
				height: headerVisible ? rem(44) : 0,
			},
			navbar: leftSidebarVisible ? leftSidebar : undefined,
			aside: rightSidebarProps,
			layout: fixedHeader ? 'default' : 'alt',
		}),
		[headerVisible, leftSidebarVisible, leftSidebar, rightSidebarProps, fixedHeader],
	);

	// useLogger({
	// 	log: 'core',
	// 	name: 'AppleLayout',
	// 	props: [
	// 		{
	// 			leftSidebarVisible,
	// 			rightSidebarProps,
	// 			rightSidebarCollapsed,
	// 			headerVisible,
	// 			loading,
	// 			cartMenu,
	// 			menuItems,
	// 			authenticated,
	// 			scrollPosition,
	// 			opacity,
	// 		},
	// 	],
	// });

	return (
		<AppShell {...appshellProps} disabled={!authenticated}>
			<AppShell.Header
				style={{
					'--header-opacity': 0.15,
				}}
				display={headerVisible ? undefined : 'none'}
				data-fixed={fixedHeader}
			>
				<HeaderContent
					hideTitle={!fixedHeader && !leftSidebarCollapsed.isCollapsed}
					hideToggleButtonWhen={fixedHeader ? undefined : 'expanded'}
					cartMenu={cartMenu}
					logoLabelSize={logoLabelSize}
					showAvatarMenu={fixedHeader}
				/>
			</AppShell.Header>

			<AppShell.Navbar
				display={leftSidebarVisible ? undefined : 'none'}
				data-fixed-header={fixedHeader}
			>
				{!fixedHeader && (
					<>
						<AppShell.Section pt={0}>
							<HeaderContent
								hideToggleButtonWhen='collapsed'
								cartMenu={cartMenu}
								logoLabelSize={logoLabelSize}
							/>
						</AppShell.Section>

						<AppShell.Section pl='xs' pr='lg' pt='xs'>
							<AvatarMenu variant={isDark ? 'dark' : 'light'} fullWidth />
						</AppShell.Section>
					</>
				)}
				<AppShell.Section grow component={ScrollArea} p='lg' pl='xs' pt='xs' pb={0}>
					<SidebarMenu size='sm' menuItems={menuItems} />
				</AppShell.Section>

				<AppShell.Section p='sm' pl='xs'>
					<AppleFooter />
				</AppShell.Section>
			</AppShell.Navbar>

			<RightSidebarPortalTarget />

			<AppShell.Main mt={headerVisible ? rem(-44) : undefined} mx={25}>
				<LoadingOverlay
					visible={loading}
					debounce={500}
					name='layout'
					zIndex={100}
					left={leftSidebarCollapsed.isCollapsed ? 0 : leftSidebar.width}
					right={rightSidebarCollapsed.isCollapsed ? (rightSidebarProps?.width ?? 0) : 0}
					loaderProps={{
						pos: 'fixed',
					}}
					overlayProps={{
						fixed: true,
					}}
				/>
				{children ?? <Outlet />}
			</AppShell.Main>
		</AppShell>
	);
}

function HeaderContent({
	hideToggleButtonWhen,
	hideTitle = false,
	hideButtons = false,
	cartMenu,
	logoLabelSize = 'xl',
	showAvatarMenu = false,
}: {
	hideToggleButtonWhen?: 'collapsed' | 'expanded';
	hideTitle?: boolean;
	hideButtons?: boolean;
	cartMenu?: React.ReactNode;
	logoLabelSize?: MantineSize | (string & {}) | number;
	showAvatarMenu?: boolean;
}) {
	const { isDark } = useColorScheme();
	const { authenticated } = useAuthContext();

	return (
		<Group
			gap={0}
			justify='space-between'
			align='baseline'
			wrap='nowrap'
			pt={rem(3)}
			px={rem(3)}
		>
			<Group gap={0} justify='space-between' align='center' wrap='nowrap'>
				{authenticated && (
					<>
						<ToggleLeftSidebarButton hideWhen={hideToggleButtonWhen} />
						{!hideTitle && (
							<AppleLogo
								labelSize={logoLabelSize}
								to='/'
								label={env.APPLE_APP_TITLE}
							/>
						)}
					</>
				)}
			</Group>
			{!hideButtons && (
				<HeaderButtons
					justify='flex-end'
					variant={isDark ? 'dark' : 'light'}
					cartMenu={cartMenu}
					showAvatarMenu={showAvatarMenu}
				/>
			)}
		</Group>
	);
}

type HeaderButtonsProps = Omit<GroupProps, 'c'> & {
	cartMenu?: React.ReactNode;
	showAvatarMenu?: boolean;
};

function HeaderButtons({ cartMenu, showAvatarMenu, variant, ...groupProps }: HeaderButtonsProps) {
	const { authenticated } = useAuthContext();

	const userAnnouncements = useUserActiveAnnouncements();

	return (
		<Group pr='xs' wrap='nowrap' gap={'xs'} {...groupProps}>
			{authenticated && cartMenu}
			{!authenticated && <ToggleColorSchemeButton />}
			{authenticated && (
				<AnnouncementMenu
					loading={userAnnouncements.isLoading}
					values={userAnnouncements.data ?? null}
				/>
			)}
			{authenticated && showAvatarMenu && (
				<AvatarMenu variant={variant === 'dark' ? 'dark' : 'light'} compact />
			)}
		</Group>
	);
}
